import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { ProjectVersion } from 'src/app/constants/project-version-constans';
import { FormatsEnums } from 'src/app/enum/formats-enums';
import { PaperSizeEnums } from 'src/app/enum/paper-size-enums';
import { FormatDetailModel } from 'src/app/models/formats/format-detail.model';
import { FormatModel } from 'src/app/models/formats/format.model';
import { MaintenanceOrderPrintModel } from 'src/app/models/maintenance/maintenance-order-print.model';
import { FormatDetailService } from 'src/app/services/formats/format-detail.service';
import { FormatService } from 'src/app/services/formats/format.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';

@Component({
  selector: 'app-print-maintenance-order-workshop',
  templateUrl: './print-maintenance-order-workshop.component.html',
  styleUrls: ['./print-maintenance-order-workshop.component.scss'],
  standalone: false
})
export class PrintMaintenanceOrderWorkshopComponent {

  @Input() companyId: number | null = 0;
  @Input() editFormat: boolean = false;
  @Input() exportFormat: boolean = false;
  @Input() focusText: boolean = false;
  @Input() formatDetail: FormatDetailModel = new FormatDetailModel();
  @Input() maintenanceOrderPrintModel: MaintenanceOrderPrintModel = new MaintenanceOrderPrintModel();
  @Input() onBlank: boolean = false;
  @Input() printDocument: boolean = false;
  @Input() reloadPrint: boolean = false;
  @Output() emitFormatDetail = new EventEmitter<FormatDetailModel>();
  @Output() finishLoadingPrintMaintenanceOrder = new EventEmitter<boolean>();

  bottomPreview: number = 0;
  dateNow: Date = new Date();
  element: any;
  elementsBody: Array<FormatDetailModel> = new Array<FormatDetailModel>();
  elementsLabel: Array<FormatDetailModel> = new Array<FormatDetailModel>();
  elementStyle: any;
  formatModel: FormatModel = new FormatModel();
  id = '';
  isShift = false;
  leftPreview: number = 0;
  listFormatDetailUpdates: Array<FormatDetailModel> = new Array<FormatDetailModel>();
  logoEnterprise: SafeUrl = "";
  mouseDown: boolean = false;
  newFormatDetail: FormatDetailModel = new FormatDetailModel();
  paperSize: number = PaperSizeEnums.letter;
  paperSizeFormat: number = 0;
  rightPreview: number = 0;
  shiftX: number = 0;
  shiftY: number = 0;
  speed = 2;
  topPreview: number = 0;
  version: string = ProjectVersion.projectVersion;

  private keydownListener!: (e: KeyboardEvent) => void;
  private mouseupListener!: () => void;
  private mousemoveListener!: (e: MouseEvent) => void;
  private keyupListener!: (e: KeyboardEvent) => void;

  constructor(private domSanitizer: DomSanitizer,
    private enterpriseService: EnterpriseService,
    private formatDetailService: FormatDetailService,
    private formatService: FormatService) {
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const reloadPrint = changes["reloadPrint"];
    const exportFormat = changes["exportFormat"];
    const formatDetail = changes["formatDetail"];

    //Válido si se está imprimiendo el documento
    if (reloadPrint && !reloadPrint.firstChange) {
      if (this.maintenanceOrderPrintModel && this.maintenanceOrderPrintModel.maintenanceOrderId != 0) {
        if (this.maintenanceOrderPrintModel.enterpriseLogo != null) await this.getLogo(this.maintenanceOrderPrintModel.enterpriseLogo);
        await this.getFormatByCompanyAndCode();
        this.listFormatDetailUpdates = new Array<FormatDetailModel>();
      }
    }

    if (exportFormat && !exportFormat.firstChange) { this.updateRangeFormatDetail(); }

    if (formatDetail && !formatDetail.firstChange) { this.changeFormatDetail(); }
  }

  ngOnDestroy() {
    // Remover event listeners globales
    if (this.keydownListener) document.removeEventListener('keydown', this.keydownListener);
    if (this.mouseupListener) document.removeEventListener('mouseup', this.mouseupListener, true);
    if (this.mousemoveListener) document.removeEventListener('mousemove', this.mousemoveListener, true);
    if (this.keyupListener) document.removeEventListener('keyup', this.keyupListener);
  }

  ngAfterViewInit() {
    if (this.editFormat) {
      this.keydownListener = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        if (
          target &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.tagName === 'SELECT' ||
            target.isContentEditable)
        ) {
          return;
        }
        // 2. Si no hay ningún elemento seleccionado para mover, no bloquear
        if (!this.element) {
          return;
        }

        // Deshabilita desplazamiento del scroll con la tecla espacio y flechas
        if (["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].indexOf(e.code) > -1) {
          this.blockScrollArrows(e);
        }

        if (e.which == 16) this.isShift = true;
        //Mover element con flechas
        if (!this.isShift) {
          switch (e.key) {
            case 'ArrowLeft': { this.moveLeft(); break; }
            case 'ArrowRight': { this.moveRight(); break; }
            case 'ArrowUp': { this.moveUp(); break; }
            case 'ArrowDown': { this.moveDown(); break; }
          }
        }

        //Modificar element tamaño de objeto
        if (this.isShift) {
          switch (e.which) {
            case 37: { this.decreaseWidth(); break; }
            case 38: { this.decreaseHeight(); break; }
            case 39: { this.increaseWidth(); break; }
            case 40: { this.increaseHeight(); break; }
          }
        }
      };

      this.mouseupListener = () => {
        this.onMouseUp();
      };

      this.mousemoveListener = (e: MouseEvent) => {
        this.onMouseMove(e);
      };

      this.keyupListener = (e: KeyboardEvent) => {
        if (e.which == 16) this.isShift = false;
      };

      // Registrar los listeners en el document
      document.addEventListener('keydown', this.keydownListener);
      document.addEventListener('mouseup', this.mouseupListener, true);
      document.addEventListener('mousemove', this.mousemoveListener, true);
      document.addEventListener('keyup', this.keyupListener);
    }
  }

  private adjustElementSize(property: 'height' | 'width', delta: number) {
    if (this.focusText) return;

    const currentValue = this.elementStyle.getPropertyValue(property);
    const numericValue = parseFloat(currentValue.replace('px', ''));
    const newValue = numericValue + delta;
    this.element.style[property] = `${newValue}px`;

    this.onClick();
  }

  private applyElementStyles(element: FormatDetailModel) {
    if (!element.visible) {
      this.element!.setAttribute('hidden', '');
      return;
    }

    const style = this.element!.style;
    style.fontSize = `${element.fontSize}px`;
    this.changeAlignment(element.alignment);
    this.changeBold(element.bold);
    this.changeBorder(element.border);

    if (element.name.includes('foot-')) {
      style.height = `${element.height}px`;
      style.width = `${element.width}px`;
    } else {
      style.backgroundColor = 'transparent';
      style.position = 'absolute';
      style.left = `${element.left}%`;
      style.top = `${element.top}%`;
      style.height = `${element.height}px`;
      style.width = `${element.width}px`;
    }
  }

  private applyHeaderBodyFooterHeights(maintenanceOrderId: number, formatModel: FormatModel) {
    const head = document.getElementById(`head-${maintenanceOrderId}`);
    const body = document.getElementById(`body-${maintenanceOrderId}`);
    const foot = document.getElementById(`foot-${maintenanceOrderId}`);
    head!.style.height = `${formatModel.head}px`;
    body!.style.height = `${formatModel.body}px`;
    foot!.style.height = `${formatModel.foot}px`;
    this.paperSizeFormat = formatModel.paperSizeId
  }

  private async applyFormatDetails(elements: FormatDetailModel[], maintenanceOrder: MaintenanceOrderPrintModel) {
    for (const element of elements) {
      this.element = document.getElementById(`${element.name}-${maintenanceOrder.maintenanceOrderId}`);
      if (this.element) {
        this.applyElementStyles(element);
      }
    }
  }

  private applyFormatStyles(element: HTMLElement) {
    element.style.fontSize = `${this.formatDetail.fontSize}px`;
    this.changeAlignment(this.formatDetail.alignment);
    this.changeBold(this.formatDetail.bold);
    this.changeBorder(this.formatDetail.border);

    if (!this.formatDetail.name.includes('foot-')) {
      element.style.position = 'absolute';
      element.style.left = `${this.formatDetail.left}%`;
      element.style.top = `${this.formatDetail.top}%`;
    }

    element.style.height = `${this.formatDetail.height}px`;
    element.style.width = `${this.formatDetail.width}px`;
  }

  private applyParagraphStyles(maintenanceOrder: MaintenanceOrderPrintModel) {
    this.elementsBody.filter(x => x.type === 'p').forEach(element => {
      this.element = document.getElementById(`${element.name}-${maintenanceOrder.maintenanceOrderId}`);
      if (this.element) {
        this.element.style.fontSize = `${element.fontSize}px`;
        this.changeAlignment(element.alignment);
        this.changeBold(element.bold);
      }
    });
  }

  blockScrollArrows(e: any) {
    if (!this.focusText) {
      e.preventDefault();
    }
  }

  changeAlignment(alignment: string) {
    this.element.style.textAlign = alignment;
  }

  changeBold(bold: string) {
    this.element.style.fontWeight = bold;
  }

  changeBorder(border: string) {
    switch (border) {
      case 'noborder': {
        this.element.style.border = '0px none rgb(0, 0, 0)';
        break;
      }
      case 'border': {
        this.element.style.border = '1px solid rgb(0, 0, 0)';
        break;
      }
      case 'underline': {
        this.element.style.border = '0px none rgb(0, 0, 0)';
        this.element.style.borderBottom = '1px solid rgb(0, 0, 0)';
        break;
      }
    }
  }

  async chargeFormat(formatModel: FormatModel): Promise<boolean> {
    return new Promise((resolved) => {
      this.formatModel = formatModel;
      this.applyHeaderBodyFooterHeights(this.maintenanceOrderPrintModel.maintenanceOrderId, formatModel);
      this.extractFormatDetails(formatModel);
      this.applyFormatDetails(formatModel.formatDetails, this.maintenanceOrderPrintModel);
      setTimeout(() => {
        this.applyParagraphStyles(this.maintenanceOrderPrintModel);
        this.finishLoadingPrintMaintenanceOrder.emit(true);
        resolved(true);
      }, 200);
    });
  }

  changeFormatDetail() {
    const formatDetailId = this.formatDetail.formatDetailId;
    if (formatDetailId == 0) return;
    const isBody = this.formatDetail.band === 'body';
    const isLabel = this.formatDetail.type === 'label';
    this.updateElementStyle(this.formatDetail.name, this.maintenanceOrderPrintModel.maintenanceOrderId);

    if (isBody) {
      const element = this.elementsBody.find(x => x.formatDetailId === formatDetailId);
      if (!element) return;

      element.alignment = this.formatDetail.alignment;
      element.fontSize = this.formatDetail.fontSize;

      if (isLabel) {
        element.visible = this.formatDetail.visible;
        element.width = this.formatDetail.width;
        element.text = this.formatDetail.text;

        if (this.formatDetail.visible) {
          this.updateElementWidth(this.formatDetail.name, this.maintenanceOrderPrintModel.maintenanceOrderId);
          this.updateElementStyle(this.formatDetail.name, this.maintenanceOrderPrintModel.maintenanceOrderId);
          this.changeAlignment(this.formatDetail.alignment);
        }
      } else {
        element.bold = this.formatDetail.bold;
        if (this.maintenanceOrderPrintModel.maintenanceOrderId) {
          this.updateElementStyle(this.formatDetail.name, this.maintenanceOrderPrintModel.maintenanceOrderId);
          this.changeAlignment(this.formatDetail.alignment);
          this.changeBold(this.formatDetail.bold);
        }
      }
      this.elementsBody = this.elementsBody.filter(x => x.formatDetailId !== formatDetailId);
      this.elementsBody.push(element);
    } else {
      this.updateElementStyle(this.formatDetail.name, this.maintenanceOrderPrintModel.maintenanceOrderId);

      if (!this.formatDetail.visible) {
        this.element.setAttribute('hidden', '');
      } else {
        this.applyFormatStyles(this.element);
        this.updateLabelText(formatDetailId, this.formatDetail.text!);
        this.listFormatDetailUpdates = this.listFormatDetailUpdates.filter(x => x.formatDetailId !== formatDetailId);
        this.listFormatDetailUpdates.push(this.formatDetail);
      }
    }
  }

  decreaseHeight() {
    this.adjustElementSize('height', -this.speed);
  }

  decreaseWidth() {
    this.adjustElementSize('width', -this.speed);
  }

  private extractFormatDetails(formatModel: FormatModel) {
    this.elementsLabel = formatModel.formatDetails.filter(x => x.type === 'label');
    this.elementsBody = formatModel.formatDetails.filter(x => x.band === 'body' && x.visible).sort((a, b) => a.orderTable - b.orderTable);
  }

  increaseHeight() {
    this.adjustElementSize('height', this.speed);
  }

  increaseWidth() {
    this.adjustElementSize('width', this.speed);
  }

  private getBorderStyle(): string {
    if (!this.elementStyle) return 'noborder';

    const sides = ['top', 'right', 'bottom', 'left'] as const;

    const hasBorder = (side: typeof sides[number]) => {
      const width = this.elementStyle.getPropertyValue(`border-${side}-width`);
      const style = this.elementStyle.getPropertyValue(`border-${side}-style`);
      return style !== 'none' && width !== '0px';
    };

    const borderStates = sides.map(side => hasBorder(side));
    const [top, right, bottom, left] = borderStates;

    if (top || right || left) {
      return 'border';
    } else if (bottom) {
      return 'underline';
    } else {
      return 'noborder';
    }
  }

  async getFormatByCompanyAndCode() {
    if (this.companyId != null && this.companyId != 0) {
      try {
        const response = await firstValueFrom(this.formatService.getFormatByCompanyAndCode(this.companyId, FormatsEnums.maintenanceOrderWorkshop));
        this.chargeFormat(response);
      } catch (error) {
        console.error('Error al obtener el formato por empresa y código.', error);
      }
    }
  }

  async getLogo(enterpriseLogo: string) {
    let blob: Blob | null = null;
    try {
      blob = await firstValueFrom(this.enterpriseService.getEnterpriseLogoAsync(enterpriseLogo));
      if (blob) {
        const objectURL = URL.createObjectURL(blob);
        this.logoEnterprise = this.domSanitizer.bypassSecurityTrustUrl(objectURL);
      }
    } catch (error) {
      console.error('Error al obtener el logo de la empresa.', error);
    }
  }

  private getStylePx(property: string): number {
    return parseFloat(this.elementStyle.getPropertyValue(property).replace('px', '')) || 0;
  }

  private getStylePxValue(property: string): number {
    const value = this.elementStyle.getPropertyValue(property);
    return parseFloat(value.replace('px', '')) || 0;
  }

  moveDown() {
    this.moveElement('top', this.speed, this.bottomPreview, true);
  }

  private moveElement(direction: 'top' | 'left', delta: number, previewLimit: number, isVertical: boolean) {
    if (this.focusText || !this.elementStyle) return;

    const currentValue = this.elementStyle.getPropertyValue(direction);
    let position = parseFloat(currentValue.replace('px', ''));

    position += delta;

    const sizeProp = isVertical ? 'height' : 'width';
    const size = parseFloat(this.elementStyle.getPropertyValue(sizeProp).replace('px', ''));

    // Restringir dentro del contenedor
    if (position < 0) position = 0;
    if (position + size > previewLimit) position = previewLimit - size;

    const percentage = ((position / previewLimit) * 100).toFixed(4);
    this.element.style[direction] = `${percentage}%`;

    this.onClick();
  }

  moveLeft() {
    this.moveElement('left', -this.speed, this.rightPreview, false);
  }

  moveRight() {
    this.moveElement('left', this.speed, this.rightPreview, false);
  }

  moveUp() {
    this.moveElement('top', -this.speed, this.bottomPreview, true);
  }

  onClick() {
    if (!this.editFormat) return;

    const name = this.id.substring(0, this.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find(x => x.name === name);
    if (!formatDetail) return;

    this.newFormatDetail = new FormatDetailModel();
    this.newFormatDetail.name = name;
    this.newFormatDetail.formatDetailId = formatDetail.formatDetailId;
    this.newFormatDetail.formatId = this.formatModel.formatId;
    this.newFormatDetail.band = formatDetail.band;
    this.newFormatDetail.description = formatDetail.description;
    this.newFormatDetail.text = formatDetail.text;
    this.newFormatDetail.visible = true;

    this.newFormatDetail.fontSize = this.getStylePxValue('font-size');
    this.newFormatDetail.bold = this.elementStyle.getPropertyValue('font-weight') === '400' ? 'normal' : 'bold';
    this.newFormatDetail.alignment = this.elementStyle.getPropertyValue('text-align');
    this.newFormatDetail.border = this.getBorderStyle();

    if (!name.includes('foot-')) {
      this.newFormatDetail.left = parseFloat(((this.getStylePxValue('left') / this.rightPreview) * 100).toFixed(4));
      this.newFormatDetail.top = parseFloat(((this.getStylePxValue('top') / this.bottomPreview) * 100).toFixed(4));
    }

    this.newFormatDetail.width = parseFloat(this.getStylePxValue('width').toFixed(4));
    this.newFormatDetail.height = parseFloat(this.getStylePxValue('height').toFixed(4));

    this.emitFormatDetail.emit(this.newFormatDetail);
    this.listFormatDetailUpdates = this.listFormatDetailUpdates.filter(x => x.formatDetailId !== this.newFormatDetail.formatDetailId);
    this.listFormatDetailUpdates.push(this.newFormatDetail);
  }

  onDragStart() {
    return false;
  }

  onMouseDown(event: any) {
    if (!this.editFormat) return;

    if (this.element)
      this.element.style.backgroundColor = 'transparent';

    this.mouseDown = true;
    this.id = event.target.id;
    this.element = document.getElementById(this.id);
    if (!this.element) return;

    this.elementStyle = window.getComputedStyle(this.element);
    this.element.style.backgroundColor = '#0d6efd6e';

    const name = this.id.substring(0, this.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find(x => x.name === name);
    if (!formatDetail) return;

    const relativeElement = document.getElementById(`${formatDetail.band}-${this.maintenanceOrderPrintModel.maintenanceOrderId}`);
    if (!relativeElement) return;

    const relativeStyle = window.getComputedStyle(relativeElement);

    this.leftPreview = Math.round(relativeElement.offsetLeft - relativeElement.scrollLeft);
    this.topPreview = Math.round(relativeElement.offsetTop - relativeElement.scrollTop);
    this.rightPreview = parseInt(relativeStyle.width.replace('px', ''));
    this.bottomPreview = parseInt(relativeStyle.height.replace('px', ''));

    if (!this.id.includes('foot-')) {
      this.element.style.cursor = 'move';

      const leftPX = event.pageX - this.leftPreview;
      const topPX = event.pageY - this.topPreview;

      this.shiftX = leftPX - this.getStylePx('left');
      this.shiftY = topPX - this.getStylePx('top');
    }

    this.onClick();
  }

  onMouseMove(event: any) {
    if (!this.mouseDown || this.id.includes('foot-')) return;

    this.element.style.position = 'absolute';

    const leftPx = Math.max(0, event.pageX - this.leftPreview - this.shiftX);
    const widthPx = this.getStylePx('width');
    const finalLeft = Math.min(leftPx, this.rightPreview - widthPx);

    const topPx = Math.max(0, event.pageY - this.topPreview - this.shiftY);
    const heightPx = this.getStylePx('height');
    const finalTop = Math.min(topPx, this.bottomPreview - heightPx);

    this.updateElementPosition(finalLeft, finalTop);
    this.onClick();
  }

  onMouseUp() {
    this.mouseDown = false;
  }

  private updateElementPosition(leftPx: number, topPx: number) {
    const leftPercentage = ((leftPx / this.rightPreview) * 100).toFixed(4);
    const topPercentage = ((topPx / this.bottomPreview) * 100).toFixed(4);

    this.element.style.left = `${leftPercentage}%`;
    this.element.style.top = `${topPercentage}%`;
  }

  private updateElementStyle(name: string, id: string | number) {
    this.element = document.getElementById(`${name}-${id}`);
    if (!this.element) return;

    this.element.style.fontSize = `${this.formatDetail.fontSize}px`;
  }

  private updateElementWidth(name: string, id: string | number) {
    this.element = document.getElementById(`${name}-${id}`);
    if (!this.element) return;
    this.element.style.width = `${this.formatDetail.width}%`;
  }

  private updateLabelText(formatId: number, text: string) {
    const labelElement = this.elementsLabel.find(x => x.formatDetailId === formatId);
    if (labelElement) { labelElement.text = text; }
  }

  async updateRangeFormatDetail() {
    try {
      const response = await firstValueFrom(this.formatDetailService.updateRangeFormatDetail(this.listFormatDetailUpdates));
      this.chargeFormat(response);
    } catch (error) {
      console.error('Error al actualizar el rango de detalles del formato.', error);
    }
  }

}
